/**
 * AdminScreen - Admin dashboard for managing agents
 */

import { useCallback, useEffect, useState } from 'react';
import type { Agent } from '../types/agent';
import {
  getAdminStats,
  getAdminAgents,
  setVerificationStatus,
  adminDeleteAgent,
  getCollections,
  createCollection,
  addAgentToCollection,
  removeAgentFromCollection,
  getCollection,
  type AdminStats,
} from '../lib/api';
import type { Collection } from '../types/agent';

interface AdminScreenProps {
  onBack: () => void;
}

const ADMIN_TOKEN_KEY = 'teleclaw_admin_token';

export function AdminScreen({ onBack }: AdminScreenProps) {
  const [adminToken, setAdminToken] = useState<string>(
    () => localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  );
  const [authenticated, setAuthenticated] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Collections state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [showCollections, setShowCollections] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [newColName, setNewColName] = useState('');
  const [newColEmoji, setNewColEmoji] = useState('📂');
  const [newColSlug, setNewColSlug] = useState('');
  const [addAgentId, setAddAgentId] = useState('');

  // Try to authenticate with stored token
  useEffect(() => {
    if (adminToken) {
      verifyToken(adminToken);
    }
  }, []);

  async function verifyToken(token: string) {
    try {
      const s = await getAdminStats(token);
      setStats(s);
      setAuthenticated(true);
      setAdminToken(token);
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
    } catch {
      setAuthenticated(false);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    }
  }

  async function handleLogin() {
    setAuthError('');
    try {
      const s = await getAdminStats(tokenInput);
      setStats(s);
      setAuthenticated(true);
      setAdminToken(tokenInput);
      localStorage.setItem(ADMIN_TOKEN_KEY, tokenInput);
    } catch {
      setAuthError('Invalid admin token');
    }
  }

  // Fetch agents when authenticated or filter changes
  useEffect(() => {
    if (!authenticated) return;
    fetchAgents();
  }, [authenticated, filter]);

  async function fetchAgents() {
    setLoading(true);
    try {
      const [agentsRes, statsRes] = await Promise.all([
        getAdminAgents(adminToken, filter),
        getAdminStats(adminToken),
      ]);
      setAgents(agentsRes.agents);
      setStats(statsRes);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }

  // Fetch collections
  async function fetchCollections() {
    try {
      const data = await getCollections();
      setCollections(data.collections);
    } catch { /* silent */ }
  }

  useEffect(() => {
    if (authenticated) fetchCollections();
  }, [authenticated]);

  async function handleCreateCollection() {
    if (!newColName || !newColSlug) return;
    try {
      await createCollection({ name: newColName, emoji: newColEmoji, slug: newColSlug }, adminToken);
      setNewColName(''); setNewColEmoji('📂'); setNewColSlug('');
      await fetchCollections();
    } catch (err) { console.error(err); }
  }

  async function handleAddAgentToCol() {
    if (!selectedCollection || !addAgentId) return;
    try {
      await addAgentToCollection(selectedCollection.id, addAgentId.trim(), adminToken);
      setAddAgentId('');
      // Refresh collection detail
      const updated = await getCollection(selectedCollection.slug);
      setSelectedCollection(updated);
    } catch (err) { console.error(err); }
  }

  async function handleRemoveAgentFromCol(agentId: string) {
    if (!selectedCollection) return;
    try {
      await removeAgentFromCollection(selectedCollection.id, agentId, adminToken);
      const updated = await getCollection(selectedCollection.slug);
      setSelectedCollection(updated);
    } catch (err) { console.error(err); }
  }

  const handleSetStatus = useCallback(async (agentId: string, status: string) => {
    setActionLoading(agentId);
    try {
      await setVerificationStatus(agentId, status, adminToken);
      await fetchAgents();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setActionLoading(null);
    }
  }, [adminToken]);

  const handleDelete = useCallback(async (agentId: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setActionLoading(agentId);
    try {
      await adminDeleteAgent(agentId, adminToken);
      await fetchAgents();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setActionLoading(null);
    }
  }, [adminToken]);

  // Login screen
  if (!authenticated) {
    return (
      <div className="max-w-md mx-auto py-12 px-4">
        <div className="text-center mb-8">
          <p className="text-4xl mb-3">🔐</p>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-[var(--tg-theme-hint-color)] text-sm mt-1">Enter admin token to continue</p>
        </div>

        <input
          type="password"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          placeholder="Admin token"
          className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] border-2 border-transparent focus:border-[var(--tg-theme-button-color)] outline-none mb-3"
        />

        {authError && (
          <p className="text-red-400 text-sm mb-3">{authError}</p>
        )}

        <button
          onClick={handleLogin}
          className="w-full px-6 py-3 bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color,#fff)] rounded-xl font-medium"
        >
          Login
        </button>

        <button
          onClick={onBack}
          className="w-full mt-3 text-sm text-[var(--tg-theme-hint-color)] hover:text-[var(--tg-theme-text-color)] transition-colors"
        >
          ← Back
        </button>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="pb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">🔧 Admin Dashboard</h1>
          <p className="text-sm text-[var(--tg-theme-hint-color)]">Manage agents & verification</p>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            setAuthenticated(false);
            setAdminToken('');
            onBack();
          }}
          className="text-sm text-red-400 hover:text-red-300"
        >
          Logout
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Total Agents</p>
          </div>
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4">
            <p className="text-2xl font-bold">{stats.todaySubmissions}</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Today</p>
          </div>
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4">
            <p className="text-2xl font-bold text-blue-400">{stats.verified}</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Verified</p>
          </div>
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-400">{stats.featured}</p>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Featured</p>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {['all', 'listed', 'verified', 'featured'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium flex-shrink-0 transition-colors ${
              filter === f
                ? 'bg-[var(--tg-theme-button-color)] text-white'
                : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'listed' && stats ? ` (${stats.listed})` : ''}
          </button>
        ))}
      </div>

      {/* Agent list */}
      {loading ? (
        <div className="text-center py-8 text-[var(--tg-theme-hint-color)]">Loading...</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-8 text-[var(--tg-theme-hint-color)]">No agents found</div>
      ) : (
        <div className="space-y-3">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4"
            >
              <div className="flex items-start gap-3 mb-3">
                {/* Logo */}
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--tg-theme-bg-color)] flex items-center justify-center overflow-hidden">
                  {agent.logo ? (
                    <img src={agent.logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg">🤖</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold truncate">{agent.name}</h3>
                    {agent.verification_status === 'featured' && <span>⭐</span>}
                    {agent.verification_status === 'verified' && <span className="text-blue-500">✓</span>}
                  </div>
                  <p className="text-xs text-[var(--tg-theme-hint-color)] truncate">
                    {agent.category} • {agent.vote_count} votes • {new Date(agent.created_at * 1000).toLocaleDateString()}
                  </p>
                  {agent.telegram_bot && (
                    <p className="text-xs text-[var(--tg-theme-hint-color)]">@{agent.telegram_bot}</p>
                  )}
                </div>

                {/* Status badge */}
                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs ${
                  agent.verification_status === 'featured'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : agent.verification_status === 'verified'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {agent.verification_status || 'listed'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {agent.verification_status !== 'verified' && (
                  <button
                    onClick={() => handleSetStatus(agent.id, 'verified')}
                    disabled={actionLoading === agent.id}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                  >
                    ✅ Verify
                  </button>
                )}
                {agent.verification_status !== 'featured' && (
                  <button
                    onClick={() => handleSetStatus(agent.id, 'featured')}
                    disabled={actionLoading === agent.id}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                  >
                    ⭐ Feature
                  </button>
                )}
                {(agent.verification_status === 'verified' || agent.verification_status === 'featured') && (
                  <button
                    onClick={() => handleSetStatus(agent.id, 'listed')}
                    disabled={actionLoading === agent.id}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 transition-colors disabled:opacity-50"
                  >
                    ↩ Unverify
                  </button>
                )}
                <button
                  onClick={() => handleDelete(agent.id, agent.name)}
                  disabled={actionLoading === agent.id}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Collections Management */}
      <div className="mt-8">
        <button
          onClick={() => setShowCollections(!showCollections)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl font-medium"
        >
          <span>📂 Manage Collections ({collections.length})</span>
          <span>{showCollections ? '▼' : '▶'}</span>
        </button>

        {showCollections && (
          <div className="mt-3 space-y-3">
            {/* Create new collection */}
            <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4">
              <h3 className="font-semibold mb-2">Create Collection</h3>
              <div className="flex gap-2 mb-2">
                <input value={newColEmoji} onChange={(e) => setNewColEmoji(e.target.value)} placeholder="📂" className="w-12 px-2 py-2 rounded-lg bg-[var(--tg-theme-bg-color)] text-center outline-none" />
                <input value={newColName} onChange={(e) => setNewColName(e.target.value)} placeholder="Collection name" className="flex-1 px-3 py-2 rounded-lg bg-[var(--tg-theme-bg-color)] outline-none" />
              </div>
              <div className="flex gap-2">
                <input value={newColSlug} onChange={(e) => setNewColSlug(e.target.value)} placeholder="slug-name" className="flex-1 px-3 py-2 rounded-lg bg-[var(--tg-theme-bg-color)] outline-none text-sm" />
                <button onClick={handleCreateCollection} className="px-4 py-2 bg-[var(--tg-theme-button-color)] text-white rounded-lg text-sm">Create</button>
              </div>
            </div>

            {/* Collection list */}
            {collections.map((col) => (
              <div key={col.id} className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3">
                <button
                  onClick={async () => {
                    if (selectedCollection?.id === col.id) {
                      setSelectedCollection(null);
                    } else {
                      const full = await getCollection(col.slug);
                      setSelectedCollection(full);
                    }
                  }}
                  className="w-full flex items-center justify-between"
                >
                  <span>{col.emoji} {col.name} <span className="text-xs text-[var(--tg-theme-hint-color)]">({col.agent_count || 0})</span></span>
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">{selectedCollection?.id === col.id ? '▼' : '▶'}</span>
                </button>

                {selectedCollection?.id === col.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--tg-theme-hint-color)]/20">
                    {/* Add agent */}
                    <div className="flex gap-2 mb-3">
                      <input value={addAgentId} onChange={(e) => setAddAgentId(e.target.value)} placeholder="Agent ID" className="flex-1 px-3 py-1.5 rounded-lg bg-[var(--tg-theme-bg-color)] outline-none text-sm" />
                      <button onClick={handleAddAgentToCol} className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs">+ Add</button>
                    </div>
                    {/* Agents in collection */}
                    {selectedCollection.agents && selectedCollection.agents.length > 0 ? (
                      <div className="space-y-2">
                        {selectedCollection.agents.map((a) => (
                          <div key={a.id} className="flex items-center justify-between text-sm">
                            <span className="truncate flex-1">{a.name}</span>
                            <button onClick={() => handleRemoveAgentFromCol(a.id)} className="text-red-400 text-xs ml-2">Remove</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--tg-theme-hint-color)]">No agents</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
